import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { subscribe, dismissToast } from './toast';
import './toast.css';

/**
 * The one place toasts are drawn. Mounted once, in App.js, inside and
 * outside the signed-in tree both -- a failed login has to be able to
 * say so.
 *
 * There were 25 <ToastContainer>s before this, one per page that
 * remembered to add it, so a message raised from a page without one
 * went nowhere at all and a message raised while two were mounted
 * could land in either.
 */

const ICONS = {
  success: (
    <path d="M20 6 9 17l-5-5" />
  ),
  error: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8v5M12 16h.01" />
    </>
  ),
  warning: (
    <>
      <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
      <path d="M12 9v4M12 17h.01" />
    </>
  ),
  info: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 16v-5M12 8h.01" />
    </>
  ),
};

const LABELS = {
  success: 'Success',
  error: 'Error',
  warning: 'Warning',
  info: 'Information',
};

const ToastItem = ({ item, onDismiss }) => {
  const [leaving, setLeaving] = useState(false);
  const [paused, setPaused] = useState(false);
  const leaveTimer = useRef(null);

  const close = () => {
    if (leaving) return;
    setLeaving(true);
    // Let the exit animation finish before the row leaves the layout;
    // removing it on the click makes the rest of the stack jump.
    leaveTimer.current = setTimeout(() => onDismiss(item.id), 160);
  };

  useEffect(() => () => clearTimeout(leaveTimer.current), []);

  // duration 0 means it stays until dismissed. `item.seq` is in the deps
  // so a repeat of the same message restarts the clock rather than
  // letting the original's timer close a message just raised again.
  useEffect(() => {
    if (!item.duration || paused) return undefined;
    const t = setTimeout(close, item.duration);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.duration, item.seq, paused]);

  /* A new notification. The strip itself is the button -- the reader's
     question is "what is it", and the answer is one press away -- with
     Dismiss beside it rather than inside it, so closing never opens. */
  if (item.type === 'notification') {
    const open = () => {
      if (item.onOpen) item.onOpen();
      close();
    };
    return (
      <div
        className={`tst tst--notif ${
          item.attendanceStatus ? `tst--att-${item.attendanceStatus}` : `tst--prio-${item.priorityTone}`
        }${leaving ? ' is-leaving' : ''}`}
        role="status"
        aria-live="polite"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocus={() => setPaused(true)}
        onBlur={() => setPaused(false)}
      >
        <button type="button" className="tst-open" onClick={open} title="Open in Notifications">
          <span className="tst-mark" aria-hidden="true">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
              <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
            </svg>
          </span>

          <span className="tst-body">
            <span className="tst-notif-head">
              <span className="tst-label">{item.source || 'New notification'}</span>
              <span className="tst-when">now</span>
              {item.attendanceStatus ? (
                <span className="tst-prio">{item.attendanceStatus === 'present' ? 'Present' : 'Absent'}</span>
              ) : item.priorityLabel && (
                <span className="tst-prio">{item.priorityLabel} priority</span>
              )}
            </span>
            <span className="tst-title">{item.text}</span>
            {item.summary && <span className="tst-summary">{item.summary}</span>}
            {item.from && <span className="tst-from">From {item.from}</span>}
          </span>
        </button>

        <button
          type="button"
          className="tst-close"
          onClick={close}
          aria-label="Dismiss"
        >
          &times;
        </button>

        {item.duration > 0 && (
          <span
            className={`tst-life${paused ? ' is-paused' : ''}`}
            key={item.seq}
            style={{ animationDuration: `${item.duration}ms` }}
            aria-hidden="true"
          />
        )}
      </div>
    );
  }

  return (
    <div
      className={`tst${leaving ? ' is-leaving' : ''} tst--${item.type}`}
      /* An error interrupts; the rest are read when the reader gets to
         them. assertive vs polite is the difference between a screen
         reader breaking off mid-sentence and waiting its turn. */
      role={item.type === 'error' ? 'alert' : 'status'}
      aria-live={item.type === 'error' ? 'assertive' : 'polite'}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <span className="tst-mark" aria-hidden="true">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          {ICONS[item.type]}
        </svg>
      </span>

      <span className="tst-body">
        <span className="tst-label">{LABELS[item.type]}</span>
        <span className="tst-text">{item.text}</span>
      </span>

      {item.count > 1 && (
        <span className="tst-count" title={`${item.count} times`}>
          {item.count > 99 ? '99+' : item.count}
        </span>
      )}

      <button
        type="button"
        className="tst-close"
        onClick={close}
        aria-label="Dismiss"
      >
        &times;
      </button>

      {/* Runs the width of the strip so the time left is read at the
          edge of vision rather than looked at. Paused with the toast. */}
      {item.duration > 0 && (
        <span
          className={`tst-life${paused ? ' is-paused' : ''}`}
          key={item.seq}
          style={{ animationDuration: `${item.duration}ms` }}
          aria-hidden="true"
        />
      )}
    </div>
  );
};

const ToastHost = () => {
  const [items, setItems] = useState([]);

  useEffect(() => subscribe(setItems), []);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <div className="tst-host" aria-live="polite" aria-relevant="additions">
      {items.map((item) => (
        <ToastItem key={item.id} item={item} onDismiss={dismissToast} />
      ))}
    </div>,
    document.body,
  );
};

export default ToastHost;
